import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Alert, AlertDescription } from './components/ui/alert';
import { 
  Heart, 
  MessageCircle, 
  Users, 
  Calendar, 
  Plus, 
  LogOut, 
  User, 
  Crown, 
  Star, 
  Sparkles, 
  Camera, 
  Image as ImageIcon,
  Video,
  Upload,
  Check,
  Clock,
  Linkedin,
  Shield,
  Building,
  MapPin,
  TrendingUp,
  AlertCircle,
  Settings,
  Edit3,
  Save,
  X,
  Bell,
  CheckCircle,
  XCircle,
  UserPlus,
  Search,
  ArrowDown,
  ArrowUp,
  UserCheck,
  RefreshCw
} from 'lucide-react';
import Webcam from 'react-webcam';
import { useDropzone } from 'react-dropzone';
import './App.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_BASE}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password, linkedinData = null) => {
    try {
      let response;
      
      if (linkedinData) {
        // Handle LinkedIn authentication data
        const { user: userData, token: authToken } = linkedinData;
        
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('token', authToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        return { success: true, data: linkedinData };
      } else {
        // For backwards compatibility or demo purposes
        response = await axios.post(`${API_BASE}/auth/login`, { email, password });
        const { user: userData, token: authToken } = response.data;
        
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('token', authToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        return { success: true };
      }
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Authentication failed' };
    }
  };

  // LinkedIn authentication for new users
  const linkedinAuth = async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/linkedin`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'LinkedIn authentication failed' };
    }
  };

  // UAE Pass authentication for new users
  const uaepassAuth = async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/uaepass`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'UAE Pass authentication failed' };
    }
  };

  // Remove register function as we only use LinkedIn
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const value = {
    user,
    token,
    login,
    linkedinAuth,
    uaepassAuth,
    logout,
    loading,
    setUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Media Upload Component
const MediaUploadComponent = ({ onUpload, onCancel }) => {
  const [showCamera, setShowCamera] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const webcamRef = React.useRef(null);

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : 'video'
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm', '.ogg', '.mov']
    },
    multiple: true
  });

  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      const newFile = {
        id: Math.random().toString(36).substr(2, 9),
        preview: imageSrc,
        type: 'image',
        captured: true
      };
      setUploadedFiles(prev => [...prev, newFile]);
      setShowCamera(false);
    }
  }, []);

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleUpload = () => {
    onUpload(uploadedFiles);
  };

  return (
    <div className="space-y-4">
      {!showCamera && (
        <>
          {/* Upload Options */}
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="outline"
              onClick={() => setShowCamera(true)}
              className="flex items-center gap-2"
              data-testid="camera-button"
            >
              <Camera className="w-4 h-4" />
              Take Photo
            </Button>
            
            <div {...getRootProps()} className="cursor-pointer">
              <input {...getInputProps()} />
              <Button
                variant="outline"
                className="flex items-center gap-2 w-full"
                data-testid="upload-button"
              >
                <Upload className="w-4 h-4" />
                Upload Files
              </Button>
            </div>
          </div>

          {/* Drag & Drop Area */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-2">
              <ImageIcon className="w-8 h-8 text-gray-400" />
              <p className="text-sm text-gray-600">
                {isDragActive ? 'Drop files here...' : 'Drag & drop images or videos here'}
              </p>
              <p className="text-xs text-gray-500">
                Supports: JPG, PNG, GIF, MP4, WebM (Max: 50MB)
              </p>
            </div>
          </div>
        </>
      )}

      {/* Camera View */}
      {showCamera && (
        <div className="space-y-4">
          <div className="relative">
            <Webcam
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              className="w-full rounded-lg"
              videoConstraints={{
                width: 640,
                height: 480,
                facingMode: "user"
              }}
            />
          </div>
          <div className="flex gap-2 justify-center">
            <Button onClick={capturePhoto} data-testid="capture-photo">
              <Camera className="w-4 h-4 mr-2" />
              Capture Photo
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowCamera(false)}
              data-testid="cancel-camera"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Preview Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium">Preview ({uploadedFiles.length} files)</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {uploadedFiles.map((file) => (
              <div key={file.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  {file.type === 'image' ? (
                    <img 
                      src={file.preview} 
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-8 h-8 text-gray-400" />
                      <span className="ml-2 text-sm text-gray-600">Video</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeFile(file.id)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-file-${file.id}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          
          {/* Upload Actions */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleUpload} data-testid="confirm-upload">
              Add to Post ({uploadedFiles.length})
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Profile Settings Component
const ProfileSettings = ({ user, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    interests: user?.interests || [],
    bio: user?.bio || '',
    location: user?.location || '',
    website: user?.website || '',
    open_to_connecting: user?.open_to_connecting !== false,
    connection_preferences: user?.connection_preferences || ['Social networking', 'Industry insights', 'Collaboration opportunities']
  });
  const [newInterest, setNewInterest] = useState('');
  const [loading, setLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const addInterest = () => {
    if (newInterest.trim() && formData.interests.length < 10 && !formData.interests.includes(newInterest.trim())) {
      setFormData({
        ...formData,
        interests: [...formData.interests, newInterest.trim()]
      });
      setNewInterest('');
    }
  };

  const removeInterest = (interestToRemove) => {
    setFormData({
      ...formData,
      interests: formData.interests.filter(interest => interest !== interestToRemove)
    });
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setPhotoUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_BASE}/users/profile/photo`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update preview and user data
      setPreviewImage(URL.createObjectURL(file));
      
      // Update the user context
      const updatedUser = { ...user, profile_image: response.data.profile_image };
      onUpdate(updatedUser);

    } catch (error) {
      console.error('Error uploading profile photo:', error);
      alert('Failed to upload profile photo. Please try again.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(`${API_BASE}/users/profile`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });
      onUpdate(response.data.user);
      onClose();
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-1">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {/* Profile Photo Section */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Profile Photo</Label>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Avatar className="w-20 h-20 border-4 border-gray-200">
                  {previewImage ? (
                    <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                  ) : user?.profile_image ? (
                    <img 
                      src={`${API_BASE}/users/profile-image/${user.profile_image.split('/').pop()}`}
                      alt="Profile" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Profile image failed to load:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : user?.linkedin_profile_picture ? (
                    <AvatarImage src={user.linkedin_profile_picture} alt={user.full_name} />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white text-xl">
                      {user?.full_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  )}
                </Avatar>
                {photoUploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <input
                  type="file"
                  id="profile-photo"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={photoUploading}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('profile-photo').click()}
                  disabled={photoUploading}
                  className="flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  {photoUploading ? 'Uploading...' : 'Change Photo'}
                </Button>
                <p className="text-xs text-gray-500">Max 5MB, any image format</p>
              </div>
            </div>
          </div>

        {/* Interests Section */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Social and Personal Interests (Max 10)</Label>
          
          {/* Current Interests - Single Row Display */}
          <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-3 border border-gray-200 rounded-lg bg-gray-50">
            {formData.interests.map((interest, index) => (
              <Badge 
                key={index} 
                className="bg-teal-100 text-teal-800 hover:bg-teal-200 cursor-pointer flex items-center gap-1"
                onClick={() => removeInterest(interest)}
              >
                {interest}
                <X className="w-3 h-3 hover:text-red-600" />
              </Badge>
            ))}
            {formData.interests.length === 0 && (
              <span className="text-gray-400 text-sm">Add your social and personal interests...</span>
            )}
          </div>
          
          {/* Add Interest Input */}
          <div className="flex gap-2">
            <Input
              value={newInterest}
              onChange={(e) => setNewInterest(e.target.value)}
              placeholder="e.g., Artificial Intelligence, Fintech, Digital Marketing..."
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addInterest();
                }
              }}
              data-testid="add-interest-input"
            />
            <Button 
              type="button" 
              onClick={addInterest}
              disabled={!newInterest.trim() || formData.interests.length >= 10}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="add-interest-button"
            >
              Add
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            {formData.interests.length}/10 interests • Click on interests to remove them
          </p>
        </div>

        {/* About Me Section */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label htmlFor="bio">About Me</Label>
            <span className="text-xs text-gray-500">
              {formData.bio ? formData.bio.trim().split(/\s+/).length : 0}/150 words
            </span>
          </div>
          <textarea
            id="bio"
            value={formData.bio}
            onChange={(e) => {
              const words = e.target.value.trim().split(/\s+/);
              if (words.length <= 150 || e.target.value.trim() === '') {
                setFormData({...formData, bio: e.target.value});
              }
            }}
            placeholder="Tell others about yourself..."
            className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            rows="4"
          />
          <p className="text-xs text-gray-500">Maximum 150 words</p>
        </div>

        {/* Location */}
        <div className="space-y-2">
          <Label htmlFor="location">Location</Label>
          <Input
            id="location"
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
            placeholder="e.g., Dubai, UAE"
            data-testid="location-input"
          />
        </div>

        {/* Website */}
        <div className="space-y-2">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            value={formData.website}
            onChange={(e) => setFormData({...formData, website: e.target.value})}
            placeholder="e.g., https://yourcompany.com"
            data-testid="website-input"
          />
        </div>

        {/* Connection Preferences */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Connection Preferences</Label>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="open-to-connecting"
                checked={formData.open_to_connecting}
                onChange={(e) => setFormData({...formData, open_to_connecting: e.target.checked})}
                className="rounded border-gray-300"
              />
              <Label htmlFor="open-to-connecting" className="text-sm">
                Open to connecting with other members
              </Label>
            </div>
          </div>
        </div>

        </div> {/* End scrollable content */}

        {/* Submit Button - Always visible */}
        <div className="flex justify-end gap-3 pt-4 border-t bg-white">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {loading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
const VerificationStatus = ({ status }) => {
  if (status === 'verified') {
    return (
      <div className="inline-flex items-center gap-1 text-xs italic text-green-600">
        <Check className="w-3 h-3" />
        Verified
      </div>
    );
  }
  
  if (status === 'under_review') {
    return (
      <div className="inline-flex items-center gap-1 text-xs italic text-amber-600">
        <Clock className="w-3 h-3" />
        Under Review
      </div>
    );
  }
  
  return null;
};



// OAuth Callback Component (handles both LinkedIn and UAE Pass)
const LinkedInCallback = () => {
  const { login } = useAuth();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const [provider, setProvider] = useState('');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const errorParam = urlParams.get('error');

        if (errorParam) {
          setError('Authentication was cancelled or failed');
          setStatus('error');
          return;
        }

        if (!code || !state) {
          setError('Invalid callback parameters');
          setStatus('error');
          return;
        }

        // Determine provider based on state or try both
        let response;
        let authProvider = 'unknown';
        
        try {
          // Try UAE Pass first (if state suggests it's UAE Pass)
          if (state.includes('uae') || state.includes('demo_state_uae')) {
            response = await axios.get(`${API_BASE}/auth/uaepass/callback`, {
              params: { code, state }
            });
            authProvider = 'UAE Pass';
            setProvider('UAE Pass');
          } else {
            // Try LinkedIn
            response = await axios.get(`${API_BASE}/auth/linkedin/callback`, {
              params: { code, state }
            });
            authProvider = 'LinkedIn';
            setProvider('LinkedIn');
          }
        } catch (firstError) {
          // If first attempt fails, try the other provider
          try {
            if (authProvider === 'UAE Pass') {
              response = await axios.get(`${API_BASE}/auth/linkedin/callback`, {
                params: { code, state }
              });
              authProvider = 'LinkedIn';
              setProvider('LinkedIn');
            } else {
              response = await axios.get(`${API_BASE}/auth/uaepass/callback`, {
                params: { code, state }
              });
              authProvider = 'UAE Pass';
              setProvider('UAE Pass');
            }
          } catch (secondError) {
            throw firstError; // Throw original error if both fail
          }
        }

        if (response.data.token) {
          // Use login function to set user and token
          const result = await login(null, null, response.data);
          
          if (result.success) {
            setStatus('success');
            // Redirect will be handled by ProtectedRoute
          } else {
            setError(result.error || 'Authentication failed');
            setStatus('error');
          }
        } else {
          setError('No authentication token received');
          setStatus('error');
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setError(error.response?.data?.detail || 'Authentication failed');
        setStatus('error');
      }
    };

    handleOAuthCallback();
  }, [login]);

  return (
    <div className="min-h-screen login-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center border-0 shadow-2xl">
        <CardContent className="p-8">
          {status === 'processing' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                {provider === 'UAE Pass' ? (
                  <Shield className="w-16 h-16 text-[#1A365D]" />
                ) : provider === 'LinkedIn' ? (
                  <Linkedin className="w-16 h-16 text-[#0077B5]" />
                ) : (
                  <Shield className="w-16 h-16 text-teal-600" />
                )}
              </div>
              <div className={`animate-spin w-8 h-8 border-4 ${
                provider === 'UAE Pass' ? 'border-[#1A365D]' : 
                provider === 'LinkedIn' ? 'border-[#0077B5]' : 
                'border-teal-600'
              } border-t-transparent rounded-full mx-auto mb-4`}></div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                Authenticating {provider ? `with ${provider}` : ''}
              </h2>
              <p className="text-gray-600">Setting up your professional profile...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-green-700 mb-4">Welcome to SureSpace!</h2>
              <p className="text-gray-600">
                Your {provider} profile has been verified successfully.
              </p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-red-700 mb-4">Authentication Failed</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button 
                onClick={() => window.location.href = '/login'}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                Try Again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Login Component
const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [linkedInAuthUrl, setLinkedInAuthUrl] = useState('');
  const [uaePassAuthUrl, setUaePassAuthUrl] = useState('');
  const [authError, setAuthError] = useState('');
  const { login, linkedinAuth, uaepassAuth } = useAuth();

  // Initialize LinkedIn authentication
  const initializeLinkedInAuth = async () => {
    setIsLoading(true);
    setAuthError('');
    
    try {
      const result = await linkedinAuth();
      
      if (result.success) {
        setLinkedInAuthUrl(result.data.auth_url);
        
        if (result.data.demo_mode) {
          // Handle demo mode - simulate LinkedIn authentication
          handleDemoAuth('linkedin');
        } else {
          // Show instructions for LinkedIn OAuth with developer tool
          setAuthError('');
          alert('You will be redirected to LinkedIn OAuth. After authorization, copy the authorization code from the LinkedIn developer tool and paste it back here.');
          
          // Redirect to LinkedIn OAuth
          window.open(result.data.auth_url, '_blank');
          
          // Show code input dialog
          setTimeout(() => {
            const authCode = prompt('Please paste the authorization code from LinkedIn OAuth redirect:');
            if (authCode) {
              handleLinkedInCodeExchange(authCode);
            }
          }, 2000);
        }
      } else {
        setAuthError(result.error);
      }
    } catch (error) {
      console.error('LinkedIn auth initialization failed:', error);
      setAuthError('Failed to initialize LinkedIn authentication. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle LinkedIn authorization code exchange
  const handleLinkedInCodeExchange = async (code) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/auth/linkedin/exchange`, { code });
      
      if (response.data.user && response.data.token) {
        const loginResult = await login(null, null, response.data);
        if (loginResult.success) {
          // Successfully logged in
          return;
        } else {
          setAuthError(loginResult.error);
        }
      } else {
        setAuthError('Invalid response from LinkedIn authentication');
      }
    } catch (error) {
      console.error('LinkedIn code exchange failed:', error);
      setAuthError('Failed to complete LinkedIn authentication. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize UAE Pass authentication
  const initializeUaePassAuth = async () => {
    setIsLoading(true);
    setAuthError('');
    
    try {
      const result = await uaepassAuth();
      
      if (result.success) {
        setUaePassAuthUrl(result.data.auth_url);
        
        if (result.data.demo_mode) {
          // Handle demo mode - simulate UAE Pass authentication
          handleDemoAuth('uaepass');
        } else {
          // Redirect to UAE Pass OAuth
          window.location.href = result.data.auth_url;
        }
      } else {
        setAuthError(result.error);
      }
    } catch (error) {
      console.error('UAE Pass auth initialization failed:', error);
      setAuthError('Failed to initialize UAE Pass authentication. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoAuth = async (provider) => {
    try {
      let callbackResponse;
      
      if (provider === 'linkedin') {
        // Simulate demo LinkedIn callback
        callbackResponse = await axios.get(`${API_BASE}/auth/linkedin/callback?code=demo_code&state=demo_state`);
      } else if (provider === 'uaepass') {
        // Simulate demo UAE Pass callback
        callbackResponse = await axios.get(`${API_BASE}/auth/uaepass/callback?code=demo_code&state=demo_state_uae`);
      }
      
      if (callbackResponse.data.token) {
        // Use the updated login function with auth data
        const result = await login(null, null, callbackResponse.data);
        
        if (!result.success) {
          setAuthError(`Demo ${provider} authentication failed. Please try again.`);
        }
        // On success, the AuthProvider will handle the redirect
      }
    } catch (error) {
      console.error(`Demo ${provider} authentication failed:`, error);
      setAuthError(`Demo ${provider} authentication failed. Please try again.`);
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-pink-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-2xl border-0 bg-white">
          <CardContent className="p-8">
            {/* Welcome Illustration */}
            <div className="text-center mb-8">
              <div className="mb-6">
                {/* Friendly illustration area */}
                <div className="w-48 h-32 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-100 to-pink-100 flex items-center justify-center relative overflow-hidden">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-500 rounded-full flex items-center justify-center">
                      <span className="text-2xl text-white">👋</span>
                    </div>
                    <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center">
                      <span className="text-xl text-white">👋</span>
                    </div>
                  </div>
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Join us today</h2>
              <p className="text-gray-600 mb-6">Enter your details to proceed further</p>
            </div>

            {authError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
                {authError}
              </div>
            )}

            {/* Main Action Buttons */}
            <div className="space-y-4">
              <Button 
                onClick={initializeUaePassAuth}
                className="w-full bg-[#E57373] hover:bg-[#D32F2F] text-white py-4 px-6 text-lg font-medium rounded-full transition-all duration-200"
                disabled={isLoading}
                data-testid="uaepass-auth-button"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-3"></div>
                    Connecting...
                  </>
                ) : (
                  'Get Started'
                )}
              </Button>
              
              <Button 
                variant="outline"
                className="w-full border-2 border-[#E57373] text-[#E57373] hover:bg-[#E57373] hover:text-white py-4 px-6 text-lg font-medium rounded-full transition-all duration-200"
                disabled={isLoading}
              >
                Sign In
              </Button>
            </div>

            {/* Features List */}
            <div className="mt-8 space-y-2 text-center">
              <div className="text-sm text-gray-500 space-y-2">
                <p>✓ Secure UAE Pass authentication</p>
                <p>✓ Safe social networking</p>
                <p>✓ Professional connections</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Dashboard
const Dashboard = () => {
  const { user, logout, setUser, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [events, setEvents] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [activeTab, setActiveTab] = useState('feed');
  const [postMedia, setPostMedia] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [followRequests, setFollowRequests] = useState([]);
  const [followedBackUsers, setFollowedBackUsers] = useState(() => {
    // Initialize from localStorage
    try {
      const stored = localStorage.getItem('followedBackUsers');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (error) {
      console.error('Error loading followed back users from localStorage:', error);
      return new Set();
    }
  });

  // Followers/Following modal state
  const [showFollowersList, setShowFollowersList] = useState(false);
  const [showFollowingList, setShowFollowingList] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [followersSearch, setFollowersSearch] = useState('');
  const [followingSearch, setFollowingSearch] = useState('');

  // Weekly Stories modal state
  const [showWeeklyStories, setShowWeeklyStories] = useState(false);
  const [weeklyStories, setWeeklyStories] = useState([]);
  const [storiesSearch, setStoriesSearch] = useState('');
  const [activeStoriesTab, setActiveStoriesTab] = useState('verified'); // 'verified' or 'rejected'

  // User Profile Modal state
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);

  // Community and Event Creation state
  const [showCreateCommunity, setShowCreateCommunity] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newCommunity, setNewCommunity] = useState({ name: '', description: '' });
  const [newEvent, setNewEvent] = useState({ title: '', description: '', event_date: '', location: '' });

  // Track sent connection requests
  const [sentConnectionRequests, setSentConnectionRequests] = useState(() => {
    try {
      const stored = localStorage.getItem('sentConnectionRequests');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (error) {
      console.error('Error loading sent connection requests from localStorage:', error);
      return new Set();
    }
  });

  // Connection requests state
  const [sentRequests, setSentRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  
  // Daily connection request limit (25 max pending)
  const DAILY_REQUEST_LIMIT = 25;
  
  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [loadingRequests, setLoadingRequests] = useState([]);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchPosts();
    fetchCommunities();
    fetchEvents();
    fetchSuggestedUsers(15, false); // Fetch more users initially
    fetchUserStats();
    fetchNotifications();
    fetchFollowRequests();
    fetchSentRequests();
    fetchUnreadCount();
  }, []);

  // Persist followedBackUsers to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('followedBackUsers', JSON.stringify([...followedBackUsers]));
    } catch (error) {
      console.error('Error saving followed back users to localStorage:', error);
    }
  }, [followedBackUsers]);

  // Persist sentConnectionRequests to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sentConnectionRequests', JSON.stringify([...sentConnectionRequests]));
    } catch (error) {
      console.error('Error saving sent connection requests to localStorage:', error);
    }
  }, [sentConnectionRequests]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_BASE}/notifications`, axiosConfig);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchFollowRequests = async () => {
    try {
      const response = await axios.get(`${API_BASE}/users/follow-requests`, axiosConfig);
      setFollowRequests(response.data);
      
      // Also set received requests for the new section
      setReceivedRequests(response.data);
    } catch (error) {
      console.error('Error fetching follow requests:', error);
    }
  };

  // Fetch sent connection requests
  const fetchSentRequests = async () => {
    try {
      const response = await axios.get(`${API_BASE}/users/sent-requests`, axiosConfig);
      setSentRequests(response.data);
    } catch (error) {
      console.error('Error fetching sent requests:', error);
      // If endpoint doesn't exist yet, we'll handle this gracefully
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API_BASE}/notifications/unread-count`, axiosConfig);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchWeeklyStories = async (status = 'verified', searchTerm = '') => {
    try {
      const params = new URLSearchParams();
      params.append('status', status);
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await axios.get(`${API_BASE}/users/my-posts?${params}`, axiosConfig);
      setWeeklyStories(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
      alert('Failed to fetch stories');
    }
  };

  const fetchFollowersList = async (searchTerm = '') => {
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await axios.get(`${API_BASE}/users/connections${params}`, axiosConfig);
      setFollowersList(response.data);
    } catch (error) {
      console.error('Error fetching connections list:', error);
      alert('Failed to fetch connections list');
    }
  };

  const fetchUserProfile = async (userId) => {
    try {
      const response = await axios.get(`${API_BASE}/users/${userId}`, axiosConfig);
      setSelectedUserProfile(response.data);
      setShowUserProfile(true);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      alert('Failed to load user profile');
    }
  };

  const fetchFollowingList = async (searchTerm = '') => {
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await axios.get(`${API_BASE}/users/connected${params}`, axiosConfig);
      setFollowingList(response.data);
    } catch (error) {
      console.error('Error fetching connected list:', error);
      alert('Failed to fetch connected list');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API_BASE}/notifications/${notificationId}/mark-read`, {}, axiosConfig);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.post(`${API_BASE}/notifications/mark-all-read`, {}, axiosConfig);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const respondToFollowRequest = async (requestId, action) => {
    try {
      const response = await axios.post(
        `${API_BASE}/users/follow-requests/${requestId}/respond`,
        { action },
        axiosConfig
      );
      
      showToast(response.data.message, 'success');
      fetchFollowRequests();   // Refresh received requests
      fetchSentRequests();     // Refresh sent requests in case status changed  
      fetchSuggestedUsers(15, false); // Refresh suggested users list
      fetchUserStats();
      fetchNotifications();
      
    } catch (error) {
      console.error('Error responding to follow request:', error);
      showToast('Failed to respond to follow request. Please try again.', 'error');
    }
  };

  // Show toast notification
  const showToast = (message, type = 'success') => {
    console.log('showToast called:', { message, type }); // Debug log
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  const sendFollowRequest = async (targetUserId) => {
    console.log('sendFollowRequest called for user:', targetUserId); // Debug log
    try {
      // Check daily limit
      if (sentRequests.length >= DAILY_REQUEST_LIMIT) {
        showToast(`Daily limit reached! You can only send ${DAILY_REQUEST_LIMIT} connection requests per day.`, 'error');
        return;
      }

      // Add to loading state
      console.log('Adding to loading state:', targetUserId); // Debug log
      setLoadingRequests(prev => {
        console.log('Previous loading requests:', prev); // Debug log
        const newLoading = [...prev, targetUserId];
        console.log('New loading requests:', newLoading); // Debug log
        return newLoading;
      });

      const response = await axios.post(`${API_BASE}/users/${targetUserId}/follow-request`, {}, axiosConfig);
      
      // Add to sent connection requests for filtering
      setSentConnectionRequests(prev => new Set([...prev, targetUserId]));
      
      // Update UI to reflect the request sent - fetch fresh suggestions to show new users
      fetchSuggestedUsers(15, false);  // Refresh the list (don't append)
      fetchSentRequests();        // Refresh sent requests list
      fetchNotifications();       // Refresh notifications to show the sent request
      
      console.log('Request successful, showing toast'); // Debug log
      showToast(response.data.message || 'Connect request sent successfully!', 'success');
    } catch (error) {
      console.error('Connect request error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send connect request. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      // Remove from loading state
      console.log('Removing from loading state:', targetUserId); // Debug log
      setLoadingRequests(prev => {
        const newLoading = prev.filter(id => id !== targetUserId);
        console.log('Final loading requests:', newLoading); // Debug log
        return newLoading;
      });
    }
  };

  const fetchSuggestedUsers = async (limit = 10, append = false) => {
    console.log('fetchSuggestedUsers called with:', { limit, append }); // Debug log
    try {
      const skip = append ? suggestedUsers.length : 0;
      console.log('Making API call with skip:', skip, 'limit:', limit); // Debug log
      
      const response = await axios.get(`${API_BASE}/users/discover?limit=${limit}&skip=${skip}`, axiosConfig);
      console.log('API response received:', response.data.length, 'users'); // Debug log
      
      if (append) {
        setSuggestedUsers(prev => {
          const newUsers = [...prev, ...response.data];
          console.log('Appending users. Previous:', prev.length, 'New total:', newUsers.length); // Debug log
          return newUsers;
        });
      } else {
        setSuggestedUsers(response.data);
        console.log('Replacing users with:', response.data.length, 'users'); // Debug log
      }
    } catch (error) {
      console.error('Error fetching suggested users:', error);
    }
  };

  const loadMoreUsers = async () => {
    console.log('loadMoreUsers called'); // Debug log
    console.log('Current suggestedUsers length:', suggestedUsers.length); // Debug log
    console.log('Current loadingMoreUsers state:', loadingMoreUsers); // Debug log
    
    setLoadingMoreUsers(true);
    try {
      await fetchSuggestedUsers(10, true); // Append 10 more users
      console.log('fetchSuggestedUsers completed'); // Debug log
    } catch (error) {
      console.error('Error in loadMoreUsers:', error); // Debug log
    } finally {
      setLoadingMoreUsers(false);
      console.log('loadMoreUsers completed, loading state reset'); // Debug log
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/users/stats`, axiosConfig);
      setUserStats(response.data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_BASE}/posts`, axiosConfig);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchCommunities = async () => {
    try {
      const response = await axios.get(`${API_BASE}/communities`, axiosConfig);
      setCommunities(response.data);
    } catch (error) {
      console.error('Error fetching communities:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_BASE}/events`, axiosConfig);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const createPost = async () => {
    if (!newPost.trim()) return;

    try {
      let imageUrls = [];
      let videoUrls = [];

      // Upload media files if any
      if (postMedia.length > 0) {
        const formData = new FormData();
        
        postMedia.forEach((media, index) => {
          if (media.file) {
            formData.append('files', media.file);
          } else if (media.captured) {
            // Convert base64 to blob for captured photos
            const base64Data = media.preview.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/jpeg' });
            formData.append('files', blob, `captured-photo-${index}.jpg`);
          }
        });

        // Upload media files
        const mediaResponse = await axios.post(`${API_BASE}/posts/media`, formData, {
          headers: {
            ...axiosConfig.headers,
            'Content-Type': 'multipart/form-data',
          },
        });

        // Process uploaded files
        mediaResponse.data.uploaded_files.forEach(file => {
          if (file.type === 'images') {
            imageUrls.push(file.path);
          } else if (file.type === 'videos') {
            videoUrls.push(file.path);
          }
        });
      }

      // Create post with media
      await axios.post(`${API_BASE}/posts`, { 
        content: newPost,
        images: imageUrls,
        videos: videoUrls
      }, axiosConfig);
      
      setNewPost('');
      setPostMedia([]);
      setShowCreatePost(false);
      setShowMediaUpload(false);
      fetchPosts();
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post. Please try again.');
    }
  };

  const likePost = async (postId) => {
    try {
      await axios.post(`${API_BASE}/posts/${postId}/like`, {}, axiosConfig);
      fetchPosts();
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleSubscription = async (planType) => {
    try {
      const response = await axios.post(`${API_BASE}/payments/checkout`, {
        plan_type: planType,
        origin_url: window.location.origin
      }, axiosConfig);
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  const handleMediaUpload = (files) => {
    setPostMedia(files);
    setShowMediaUpload(false);
  };

  const followUser = async (userId) => {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }
      
      await axios.post(`${API_BASE}/users/${userId}/follow`, {}, axiosConfig);
      fetchSuggestedUsers(); // Refresh the suggested users
      fetchUserStats(); // Refresh user stats
      // Don't refresh notifications immediately to preserve local state
      
      return true; // Return success status
    } catch (error) {
      console.error('Error following user:', error);
      alert('Failed to follow user. Please try again.');
      return false;
    }
  };

  const unfollowUser = async (userId) => {
    try {
      await axios.post(`${API_BASE}/users/${userId}/unfollow`, {}, axiosConfig);
      fetchSuggestedUsers(); // Refresh the suggested users  
      fetchUserStats(); // Refresh user stats
    } catch (error) {
      console.error('Error unfollowing user:', error);
      alert('Failed to unfollow user. Please try again.');
    }
  };

  const handleProfileUpdate = async (updatedUser) => {
    // Update user context/state with new profile data
    setUser(prevUser => ({ ...prevUser, ...updatedUser }));
    fetchUserStats(); // Refresh stats
  };

  const quickFollow = async (targetUserId) => {
    try {
      await followUser(targetUserId);
      // Show success feedback
      alert('Successfully connected! You are now following this professional.');
    } catch (error) {
      console.error('Quick follow error:', error);
    }
  };

  const sendConnectionRequest = async (targetUserId) => {
    try {
      const response = await axios.post(`${API_BASE}/users/${targetUserId}/connect`, {}, axiosConfig);
      
      // Add to sent connection requests
      setSentConnectionRequests(prev => new Set([...prev, targetUserId]));
      
      // Update UI to reflect the connection
      fetchSuggestedUsers();
      fetchUserStats();
      fetchNotifications();  // Refresh notifications to show the sent request
      
      alert(response.data.message || 'Connection request sent successfully!');
    } catch (error) {
      console.error('Connection request error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send connection request. Please try again.';
      alert(errorMessage);
    }
  };

  // Community creation function
  const createCommunity = async () => {
    if (!newCommunity.name.trim() || !newCommunity.description.trim()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      await axios.post(`${API_BASE}/communities`, newCommunity, axiosConfig);
      
      setNewCommunity({ name: '', description: '' });
      setShowCreateCommunity(false);
      fetchCommunities();
      showToast('Community created successfully!', 'success');
    } catch (error) {
      console.error('Error creating community:', error);
      showToast('Failed to create community. Please try again.', 'error');
    }
  };

  // Event creation function
  const createEvent = async () => {
    if (!newEvent.title.trim() || !newEvent.description.trim() || !newEvent.event_date) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      await axios.post(`${API_BASE}/events`, newEvent, axiosConfig);
      
      setNewEvent({ title: '', description: '', event_date: '', location: '' });
      setShowCreateEvent(false);
      fetchEvents();
      showToast('Event created successfully!', 'success');
    } catch (error) {
      console.error('Error creating event:', error);
      showToast('Failed to create event. Please try again.', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#E57373] to-[#F06292] shadow-lg border-b border-pink-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Title */}
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  SureSpace
                </h1>
                <p className="text-xs text-white/80">Safe Social Networking</p>
              </div>
            </div>

            {/* Center Navigation */}
            <div className="flex-1 flex justify-center max-w-2xl mx-8">
              {/* Main Navigation */}
              <nav className="hidden md:flex space-x-3">
                <Button
                  variant={activeTab === 'feed' ? 'default' : 'ghost'}
                  className={`px-6 py-2.5 ${
                    activeTab === 'feed' ? 'bg-teal-500 hover:bg-teal-600 text-white' : 'hover:bg-teal-50 text-gray-700'
                  }`}
                  onClick={() => setActiveTab('feed')}
                  data-testid="feed-tab"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Feed
                </Button>
                <Button
                  variant={activeTab === 'notifications' ? 'default' : 'ghost'}
                  className={`px-6 py-2.5 relative ${
                    activeTab === 'notifications' ? 'bg-teal-500 hover:bg-teal-600 text-white' : 'hover:bg-teal-50 text-gray-700'
                  }`}
                  onClick={() => setActiveTab('notifications')}
                  data-testid="notifications-tab"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notifications
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-xs px-1 min-w-[20px] h-5 flex items-center justify-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Button>
                <Button
                  variant={activeTab === 'communities' ? 'default' : 'ghost'}
                  className={`px-6 py-2.5 ${
                    activeTab === 'communities' ? 'bg-teal-500 hover:bg-teal-600 text-white' : 'hover:bg-teal-50 text-gray-700'
                  }`}
                  onClick={() => setActiveTab('communities')}
                  data-testid="communities-tab"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Communities
                </Button>
                <Button
                  variant={activeTab === 'events' ? 'default' : 'ghost'}
                  className={`px-6 py-2.5 ${
                    activeTab === 'events' ? 'bg-teal-500 hover:bg-teal-600 text-white' : 'hover:bg-teal-50 text-gray-700'
                  }`}
                  onClick={() => setActiveTab('events')}
                  data-testid="events-tab"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Events
                </Button>
              </nav>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              {/* Mobile Menu */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="md:hidden">
                    <Users className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Navigation</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <Button
                      variant={activeTab === 'feed' ? 'default' : 'ghost'}
                      className={`w-full justify-start py-2.5 ${
                        activeTab === 'feed' ? 'bg-teal-500 hover:bg-teal-600' : 'hover:bg-teal-50'
                      }`}
                      onClick={() => setActiveTab('feed')}
                      data-testid="mobile-feed-tab"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Feed
                    </Button>
                    <Button
                      variant={activeTab === 'notifications' ? 'default' : 'ghost'}
                      className={`w-full justify-start relative py-2.5 ${
                        activeTab === 'notifications' ? 'bg-teal-500 hover:bg-teal-600' : 'hover:bg-teal-50'
                      }`}
                      onClick={() => setActiveTab('notifications')}
                      data-testid="mobile-notifications-tab"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Notifications
                      {unreadCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white rounded-full text-xs px-2 py-1">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Button>
                    <Button
                      variant={activeTab === 'communities' ? 'default' : 'ghost'}
                      className={`w-full justify-start py-2.5 ${
                        activeTab === 'communities' ? 'bg-teal-500 hover:bg-teal-600' : 'hover:bg-teal-50'
                      }`}
                      onClick={() => setActiveTab('communities')}
                      data-testid="mobile-communities-tab"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Communities
                    </Button>
                    <Button
                      variant={activeTab === 'events' ? 'default' : 'ghost'}
                      className={`w-full justify-start py-2.5 ${
                        activeTab === 'events' ? 'bg-teal-500 hover:bg-teal-600' : 'hover:bg-teal-50'
                      }`}
                      onClick={() => setActiveTab('events')}
                      data-testid="mobile-events-tab"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Events
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Premium Badge */}
              {user?.subscription?.type === 'premium' && (
                <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0 px-3 py-1">
                  <Crown className="w-3 h-3 mr-1" />
                  Premium
                </Badge>
              )}

              {/* Verification Badges */}
              {user?.linkedin_verified && (
                <Badge className="bg-[#0077B5] text-white" data-testid="linkedin-verified-badge">
                  <Linkedin className="w-3 h-3 mr-1" />
                  LinkedIn
                </Badge>
              )}

              {user?.uaepass_verified && (
                <Badge className="bg-green-600 text-white" data-testid="uaepass-verified-badge">
                  <Shield className="w-3 h-3 mr-1" />
                  UAE Pass
                </Badge>
              )}

              {/* Upgrade Button */}
              <Button
                onClick={() => setShowSubscription(true)}
                variant="outline"
                className="hidden sm:flex border-teal-500 text-teal-600 hover:bg-teal-50 px-4"
                data-testid="upgrade-button"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Upgrade
              </Button>
              
              {/* User Avatar */}
              <Avatar data-testid="user-avatar" className="h-8 w-8">
                {user?.profile_image ? (
                  <AvatarImage src={`${API_BASE}/users/profile-image/${user.profile_image}`} alt={user?.full_name} />
                ) : (
                  <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white">
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                )}
              </Avatar>
              
              {/* Logout Button */}
              <Button variant="ghost" onClick={logout} data-testid="logout-button" size="sm">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* User Profile Sidebar */}
          {/* User Profile Section - Compact Design */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-lg sticky top-4" style={{backgroundColor: '#F0F1FF'}}>
              <CardContent className="p-4">
                <div className="text-center space-y-3">
                  {/* Compact Profile Photo */}
                  <div className="relative inline-block">
                    <Avatar className="w-16 h-16 mx-auto border-2 border-white shadow-lg">
                      {user?.profile_image ? (
                        <img 
                          src={`${API_BASE}/users/profile-image/${user.profile_image.split('/').pop()}`}
                          alt={user.full_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('Profile image failed to load:', e.target.src);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : user?.linkedin_profile_picture ? (
                        <AvatarImage src={user.linkedin_profile_picture} alt={user.full_name} />
                      ) : (
                        <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white text-xl">
                          {user?.full_name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    {user?.profile_verified && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                        <Shield className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>

                  {/* Compact User Info */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <h2 className="font-bold text-lg truncate flex-1">{user?.full_name}</h2>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowProfileSettings(true)}
                        className="p-1 h-8 w-8"
                        data-testid="profile-settings-button"
                      >
                        <Settings className="w-4 h-4 text-gray-600" />
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 truncate">@{user?.username}</p>
                    {user?.linkedin_headline && (
                      <p className="text-xs text-gray-600 truncate">{user.linkedin_headline}</p>
                    )}
                  </div>

                  {/* Enhanced Stats Row */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                    <div className="grid grid-cols-3 divide-x divide-gray-300">
                      <button 
                        className="flex flex-col items-center p-3 rounded-lg hover:bg-white/70 transition-all duration-200 group -ml-3"
                        onClick={() => {
                          fetchFollowersList();
                          setShowFollowersList(true);
                        }}
                      >
                        <div className="text-2xl font-bold text-teal-600 group-hover:text-teal-700 transition-colors">
                          {userStats?.followers_count || 0}
                        </div>
                        <div className="text-xs text-gray-600 font-medium mt-1">Connections</div>
                      </button>
                      
                      <button 
                        className="flex flex-col items-center p-3 rounded-lg hover:bg-white/70 transition-all duration-200 group"
                        onClick={() => {
                          fetchFollowingList();
                          setShowFollowingList(true);
                        }}
                      >
                        <div className="text-2xl font-bold text-teal-600 group-hover:text-teal-700 transition-colors">
                          {userStats?.following_count || 0}
                        </div>
                        <div className="text-xs text-gray-600 font-medium mt-1">Connected</div>
                      </button>
                      
                      <button 
                        className="flex flex-col items-center p-3 rounded-lg hover:bg-white/70 transition-all duration-200 group -mr-3"
                        onClick={() => {
                          setActiveStoriesTab('verified');
                          fetchWeeklyStories('verified');
                          setShowWeeklyStories(true);
                        }}
                      >
                        <div className="text-2xl font-bold text-teal-600 group-hover:text-teal-700 transition-colors">
                          {userStats?.posts_count || 0}
                        </div>
                        <div className="text-xs text-gray-600 font-medium mt-1">Posts</div>
                      </button>
                    </div>
                  </div>

                  {/* Connection Status & Interests */}
                  <div className="space-y-4">
                    {/* Connection Status */}
                    <div className="text-center">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                        user?.open_to_connecting !== false 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-orange-100 text-orange-800 border border-orange-200'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          user?.open_to_connecting !== false ? 'bg-green-500' : 'bg-orange-500'
                        }`}></div>
                        {user?.open_to_connecting !== false ? 'Open to connecting' : 'Taking a break'}
                      </div>
                    </div>

                    {/* Enhanced Interests */}
                    <div className="text-center">
                      <h4 className="font-semibold text-sm text-gray-700 mb-3">Top Interests</h4>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {user?.interests?.slice(0, 3).map((interest, index) => (
                          <Badge 
                            key={index} 
                            className="bg-gradient-to-r from-teal-50 to-blue-50 text-teal-700 border border-teal-200 px-3 py-1.5 text-xs font-medium hover:from-teal-100 hover:to-blue-100 transition-colors"
                          >
                            {interest}
                          </Badge>
                        )) || (
                          <span className="text-xs text-gray-400 py-2">No interests added yet</span>
                        )}
                      </div>
                    </div>

                    {/* Connect With Me Button - Enhanced */}
                    {user?.open_to_connecting !== false && (
                      <div className="text-center">
                        <Button
                          size="sm"
                          disabled
                          className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-2 rounded-full shadow-sm cursor-default opacity-80 hover:opacity-90 transition-opacity"
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Connect With Me
                        </Button>
                      </div>
                    )}
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* Grow Connections Section - Enhanced */}
            <Card className="border-0 shadow-lg mt-4" style={{backgroundColor: '#F0F1FF'}}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm">Grow Connections</h3>
                  <Users className="w-4 h-4 text-gray-400" />
                </div>
                
                {/* Daily Limit Indicator */}
                <div className="mb-4 p-2 bg-white rounded-lg border">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Daily Requests:</span>
                    <span className={`font-semibold ${sentRequests.length >= DAILY_REQUEST_LIMIT ? 'text-red-600' : 'text-teal-600'}`}>
                      {sentRequests.length}/{DAILY_REQUEST_LIMIT}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                    <div 
                      className={`h-1.5 rounded-full ${sentRequests.length >= DAILY_REQUEST_LIMIT ? 'bg-red-500' : 'bg-teal-500'}`}
                      style={{width: `${Math.min((sentRequests.length / DAILY_REQUEST_LIMIT) * 100, 100)}%`}}
                    ></div>
                  </div>
                  {sentRequests.length >= DAILY_REQUEST_LIMIT && (
                    <p className="text-xs text-red-600 mt-1">Daily limit reached. Try again tomorrow!</p>
                  )}
                </div>

                <div className="space-y-3">
                  {suggestedUsers && suggestedUsers.length > 0 ? (
                    suggestedUsers
                      .filter(suggestedUser => !sentConnectionRequests.has(suggestedUser.id))
                      .map(suggestedUser => (
                      <div key={suggestedUser.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                        <Avatar className="w-10 h-10">
                          {suggestedUser.linkedin_profile_picture ? (
                            <AvatarImage src={suggestedUser.linkedin_profile_picture} alt={suggestedUser.full_name} />
                          ) : (
                            <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white text-sm">
                              {suggestedUser.full_name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <p className="font-medium text-sm truncate">{suggestedUser.full_name}</p>
                            {suggestedUser.uaepass_verified && (
                              <Badge className="bg-[#1A365D] text-white text-xs px-1 py-0">
                                <Shield className="w-2 h-2 mr-1" />
                                UAE
                              </Badge>
                            )}
                            {suggestedUser.linkedin_verified && !suggestedUser.uaepass_verified && (
                              <Badge className="bg-[#0077B5] text-white text-xs px-1 py-0">
                                <Linkedin className="w-2 h-2 mr-1" />
                                IN
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-gray-400">
                            <span>{suggestedUser.followers_count} connections</span>
                            {suggestedUser.common_interests > 0 && (
                              <span> • {suggestedUser.common_interests} mutual interests</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          {suggestedUser.is_following ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => unfollowUser(suggestedUser.id)}
                              className="text-xs px-3 py-2 h-8 text-gray-600 border-gray-300"
                            >
                              Connected
                            </Button>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                onClick={() => {
                                  console.log('Connect button clicked for user:', suggestedUser.id); // Debug log
                                  console.log('Current loading requests:', loadingRequests); // Debug log
                                  console.log('Current sent requests length:', sentRequests.length); // Debug log
                                  if (sentRequests.length >= DAILY_REQUEST_LIMIT) {
                                    showToast(`Daily limit reached! You can only send ${DAILY_REQUEST_LIMIT} connection requests per day.`, 'error');
                                    return;
                                  }
                                  sendFollowRequest(suggestedUser.id);
                                }}
                                className="text-xs px-3 py-2 h-8 bg-teal-500 hover:bg-teal-600 text-white rounded-md"
                                disabled={sentRequests.length >= DAILY_REQUEST_LIMIT || loadingRequests.includes(suggestedUser.id)}
                              >
                                {loadingRequests.includes(suggestedUser.id) ? (
                                  <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Sending...</span>
                                  </div>
                                ) : (
                                  'Connect'
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendFollowRequest(suggestedUser.id)}
                                className="text-xs px-3 py-1.5 h-7 text-teal-600 border-teal-200 hover:bg-teal-50 rounded-md"
                                disabled={sentRequests.length >= DAILY_REQUEST_LIMIT || loadingRequests.includes(suggestedUser.id)}
                              >
                                {loadingRequests.includes(suggestedUser.id) ? (
                                  <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                                  </div>
                                ) : (
                                  'Request'
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">No users found</p>
                      <p className="text-xs text-gray-400">Check back later for new members</p>
                    </div>
                  )}
                  
                  {/* Load More Button */}
                  {suggestedUsers && suggestedUsers.filter(u => !sentConnectionRequests.has(u.id)).length > 0 && (
                    <div className="text-center pt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          console.log('Load More button clicked'); // Debug log
                          loadMoreUsers();
                        }}
                        className="text-xs px-4 py-2 text-teal-600 border-teal-200 hover:bg-teal-50"
                        disabled={loadingMoreUsers}
                      >
                        {loadingMoreUsers ? (
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                            <span>Loading...</span>
                          </div>
                        ) : (
                          <>
                            <RefreshCw className="w-3 h-3 mr-2" />
                            Load More
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Create Story */}
            {activeTab === 'feed' && user?.profile_verified && (
              <Card className="mb-6 border-0 shadow-lg" style={{backgroundColor: '#F0F1FF'}}>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <Avatar>
                      <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white">
                        {user?.full_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <Button
                      variant="outline"
                      className="flex-1 justify-start text-gray-500 hover:bg-teal-50 border-teal-200"
                      onClick={() => setShowCreatePost(true)}
                      data-testid="create-post-button"
                    >
                      What's on your mind
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowMediaUpload(true)}
                      className="border-teal-200 hover:bg-teal-50"
                      data-testid="add-media-button"
                    >
                      <ImageIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stories */}
            {activeTab === 'feed' && (
              <div className="space-y-6" data-testid="posts-feed">
                {posts.map((post) => (
                  <Card key={post.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow" style={{backgroundColor: '#F0F1FF'}}>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <Avatar className="w-10 h-10">
                          {post.author_profile_image ? (
                            <AvatarImage src={`${API_BASE}/users/profile-image/${post.author_profile_image}`} alt={post.author_full_name} />
                          ) : (
                            <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white">
                              {post.author_full_name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => fetchUserProfile(post.author_id)}
                                className="font-semibold text-gray-900 hover:text-teal-600 cursor-pointer transition-colors"
                              >
                                {post.author_full_name}
                              </button>
                              <span className="text-gray-500 text-sm">
                                {new Date(post.created_at).toLocaleDateString()}
                              </span>
                              <VerificationStatus status={post.verification_status} />
                            </div>
                          </div>
                          <p className="text-gray-800 mb-4 leading-relaxed">{post.content}</p>
                          
                          {/* Display Images */}
                          {post.images && post.images.length > 0 && (
                            <div className="mb-4">
                              <div className={`grid gap-2 ${
                                post.images.length === 1 ? 'grid-cols-1' : 
                                post.images.length === 2 ? 'grid-cols-2' : 
                                'grid-cols-2 md:grid-cols-3'
                              }`}>
                                {post.images.map((image, index) => (
                                  <div key={index} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                                    <img 
                                      src={`${API_BASE.replace('/api', '')}/${image}`}
                                      alt={`Post image ${index + 1}`}
                                      className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                                      onClick={() => window.open(`${API_BASE.replace('/api', '')}/${image}`, '_blank')}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Display Videos */}
                          {post.videos && post.videos.length > 0 && (
                            <div className="mb-4">
                              <div className="grid gap-2 grid-cols-1">
                                {post.videos.map((video, index) => (
                                  <div key={index} className="rounded-lg overflow-hidden bg-gray-100">
                                    <video 
                                      src={`${API_BASE.replace('/api', '')}/${video}`}
                                      controls
                                      className="w-full max-h-96"
                                    >
                                      Your browser does not support the video tag.
                                    </video>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center space-x-6">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => likePost(post.id)}
                              className="text-gray-500 hover:text-red-500 hover:bg-red-50"
                              data-testid={`like-post-${post.id}`}
                            >
                              <Heart className={`w-4 h-4 mr-2 ${post.likes?.includes(user?.id) ? 'fill-red-500 text-red-500' : ''}`} />
                              {post.likes?.length || 0}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-gray-500 hover:text-blue-500 hover:bg-blue-50"
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              {post.comments?.length || 0}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Notifications */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                {/* Notifications Header */}
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-800">Notifications</h2>
                  {unreadCount > 0 && (
                    <Button
                      onClick={markAllNotificationsRead}
                      variant="outline"
                      size="sm"
                      className="text-teal-600 border-teal-200 hover:bg-teal-50"
                    >
                      Mark All Read
                    </Button>
                  )}
                </div>

                {/* Connection Requests Management Section */}
                <Card className="border-0 shadow-lg" style={{backgroundColor: '#F0F1FF'}}>
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Connection Requests
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Received Requests Section */}
                    <div>
                      <h3 className="font-semibold text-md mb-3 flex items-center gap-2">
                        <ArrowDown className="w-4 h-4 text-teal-600" />
                        Received Requests ({receivedRequests.length})
                      </h3>
                      {receivedRequests.length > 0 ? (
                        <div className="space-y-3">
                          {receivedRequests.map((request) => (
                            <div key={request.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-teal-100">
                              <div className="flex items-center gap-3">
                                <Avatar className="w-10 h-10">
                                  {request.from_user_avatar ? (
                                    <AvatarImage src={request.from_user_avatar} alt={request.from_user_name} />
                                  ) : (
                                    <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white">
                                      {request.from_user_name?.charAt(0) || 'U'}
                                    </AvatarFallback>
                                  )}
                                </Avatar>
                                <div>
                                  <p className="font-medium text-sm">{request.from_user_name}</p>
                                  <p className="text-xs text-gray-500">
                                    Wants to connect • {new Date(request.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => respondToFollowRequest(request.id, 'accept')}
                                  className="bg-teal-500 hover:bg-teal-600 text-white"
                                  data-testid={`accept-request-${request.id}`}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => respondToFollowRequest(request.id, 'decline')}
                                  className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                  data-testid={`decline-request-${request.id}`}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Decline
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm">No pending connection requests</p>
                        </div>
                      )}
                    </div>

                    {/* Sent Requests Section */}
                    <div>
                      <h3 className="font-semibold text-md mb-3 flex items-center gap-2">
                        <ArrowUp className="w-4 h-4 text-blue-600" />
                        Sent Requests ({sentRequests.length})
                      </h3>
                      {sentRequests.length > 0 ? (
                        <div className="space-y-3">
                          {sentRequests.map((request) => (
                            <div key={request.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex items-center gap-3">
                                <Avatar className="w-10 h-10">
                                  <AvatarFallback className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                                    {request.to_user_name?.charAt(0) || 'U'}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-sm">{request.to_user_name}</p>
                                  <p className="text-xs text-gray-500">
                                    Request sent • {new Date(request.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-yellow-100 text-yellow-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm">No sent connection requests</p>
                          <p className="text-xs text-gray-400">Send connection requests to grow your network</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Connection Requests Section */}
                {followRequests.length > 0 && (
                  <Card className="border-0 shadow-lg" style={{backgroundColor: '#F0F1FF'}}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-teal-600" />
                        Connect Requests ({followRequests.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {followRequests.map((request) => (
                        <div key={request.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                          <Avatar className="w-12 h-12">
                            {request.from_user_avatar ? (
                              <AvatarImage src={request.from_user_avatar} alt={request.from_user_name} />
                            ) : (
                              <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white">
                                {request.from_user_name?.charAt(0) || 'U'}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium">{request.from_user_name}</p>
                            <p className="text-sm text-gray-600">wants to follow you</p>
                            <p className="text-xs text-gray-400">
                              {new Date(request.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => respondToFollowRequest(request.id, 'accept')}
                              size="sm"
                              className="bg-teal-500 hover:bg-teal-600 text-white"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Accept
                            </Button>
                            <Button
                              onClick={() => respondToFollowRequest(request.id, 'decline')}
                              size="sm"
                              variant="outline"
                              className="text-gray-600 border-gray-300 hover:bg-gray-50"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* All Notifications */}
                <Card className="border-0 shadow-lg" style={{backgroundColor: '#F0F1FF'}}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="w-5 h-5 text-teal-600" />
                      All Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {notifications.length > 0 ? (
                      notifications.map((notification) => (
                        <div 
                          key={notification.id} 
                          className={`flex items-start gap-4 p-4 rounded-lg cursor-pointer transition-colors ${
                            !notification.read ? 'bg-teal-50 border-l-4 border-teal-500' : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                          onClick={() => !notification.read && markNotificationRead(notification.id)}
                        >
                          <Avatar className="w-10 h-10">
                            {notification.from_user_avatar ? (
                              <AvatarImage src={notification.from_user_avatar} alt={notification.from_user_name} />
                            ) : (
                              <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white">
                                {notification.from_user_name?.charAt(0) || 'U'}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm">{notification.title}</h4>
                              {!notification.read && (
                                <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              {new Date(notification.created_at).toLocaleDateString()}
                            </p>
                            
                            {/* Connect Back Button for follow notifications */}
                            {notification.type === 'follow_back' && (
                              <Button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  
                                  const userIdToFollow = notification.action_data?.follow_back_user_id;
                                  const isAlreadyFollowed = followedBackUsers.has(userIdToFollow);
                                  
                                  if (isAlreadyFollowed) return; // Prevent multiple clicks
                                  
                                  if (userIdToFollow) {
                                    const success = await followUser(userIdToFollow);
                                    if (success) {
                                      // Add to followed back users set and persist to localStorage
                                      setFollowedBackUsers(prev => {
                                        const newSet = new Set([...prev, userIdToFollow]);
                                        return newSet;
                                      });
                                      alert(`Successfully connected with ${notification.from_user_name} back!`);
                                    }
                                  } else {
                                    alert('Error: Cannot find user to connect back');
                                  }
                                }}
                                size="sm"
                                variant={followedBackUsers.has(notification.action_data?.follow_back_user_id) ? "default" : "outline"}
                                disabled={followedBackUsers.has(notification.action_data?.follow_back_user_id)}
                                className={`mt-2 ${
                                  followedBackUsers.has(notification.action_data?.follow_back_user_id)
                                    ? 'bg-green-500 hover:bg-green-600 text-white cursor-default' 
                                    : 'text-teal-600 border-teal-200 hover:bg-teal-50'
                                }`}
                              >
                                {followedBackUsers.has(notification.action_data?.follow_back_user_id) ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Connected
                                  </>
                                ) : (
                                  'Connect Back'
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No notifications yet</p>
                        <p className="text-sm text-gray-400">When people interact with you, you'll see it here</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Communities */}
            {activeTab === 'communities' && (
              <div className="space-y-6">
                {/* Communities Header with Create Button */}
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-800">Communities</h2>
                  <Button 
                    onClick={() => setShowCreateCommunity(true)}
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    data-testid="create-community-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Community
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="communities-grid">
                  {communities.map((community) => (
                    <Card key={community.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow" style={{backgroundColor: '#F0F1FF'}}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-teal-400 to-blue-500 rounded-lg flex items-center justify-center">
                            <Users className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-2">{community.name}</h3>
                            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{community.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-500">
                                <Users className="w-4 h-4 inline mr-1" />
                                {community.members?.length || 0} members
                              </span>
                              <Button 
                                size="sm" 
                                className="bg-teal-500 hover:bg-teal-600"
                                data-testid={`join-community-${community.id}`}
                              >
                                Join
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Events */}
            {activeTab === 'events' && (
              <div className="space-y-6">
                {/* Events Header with Create Button */}
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-800">Events</h2>
                  <Button 
                    onClick={() => setShowCreateEvent(true)}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="create-event-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="events-grid">
                  {events.map((event) => (
                    <Card key={event.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow" style={{backgroundColor: '#F0F1FF'}}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-500 rounded-lg flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-2">{event.title}</h3>
                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{event.description}</p>
                            <div className="space-y-2 text-sm text-gray-500 mb-4">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                <span>Host: {event.host_username}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span>{new Date(event.event_date).toLocaleDateString()}</span>
                              </div>
                              {event.location && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4" />
                                  <span>{event.location}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-500">
                                <Users className="w-4 h-4 inline mr-1" />
                                {event.attendees?.length || 0} attending
                              </span>
                              <Button 
                                size="sm"
                                className="bg-blue-500 hover:bg-blue-600"
                                data-testid={`attend-event-${event.id}`}
                              >
                                Attend
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Post Dialog */}
      <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
        <DialogContent 
          data-testid="create-post-dialog" 
          aria-describedby="create-post-description"
          className="max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle>Share Professional Update</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" id="create-post-description">
            <Textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder="What's on your mind"
              rows={4}
              className="resize-none"
              data-testid="post-content-input"
            />
            
            {postMedia.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {postMedia.map((media) => (
                  <div key={media.id} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={media.preview} 
                      alt="Post media"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex items-center justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setShowMediaUpload(true)}
                className="flex items-center gap-2"
                data-testid="add-media-to-post"
              >
                <ImageIcon className="w-4 h-4" />
                Add Media
              </Button>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowCreatePost(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={createPost} 
                  disabled={!newPost.trim()}
                  className="bg-teal-500 hover:bg-teal-600"
                  data-testid="publish-post-button"
                >
                  Post
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Upload Dialog */}
      <Dialog open={showMediaUpload} onOpenChange={setShowMediaUpload}>
        <DialogContent className="max-w-4xl" data-testid="media-upload-dialog">
          <DialogHeader>
            <DialogTitle>Add Photos & Videos</DialogTitle>
          </DialogHeader>
          <MediaUploadComponent
            onUpload={handleMediaUpload}
            onCancel={() => setShowMediaUpload(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={showSubscription} onOpenChange={setShowSubscription}>
        <DialogContent 
          data-testid="subscription-dialog" 
          aria-describedby="subscription-description"
          className="max-w-4xl"
        >
          <DialogHeader>
            <DialogTitle>Upgrade to Premium</DialogTitle>
          </DialogHeader>
          <div className="space-y-6" id="subscription-description">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Join Dubai's Premium Professional Network
              </h3>
              <p className="text-gray-600">
                Ad-free, verified connections with Dubai's top professionals
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-gray-200 hover:border-teal-300 transition-colors">
                <CardContent className="p-8 text-center">
                  <h3 className="font-semibold text-xl mb-2">Monthly</h3>
                  <div className="text-4xl font-bold mb-2">
                    AED 30
                    <span className="text-lg text-gray-500 font-normal">/month</span>
                  </div>
                  <ul className="text-sm space-y-3 mb-8 text-left">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>AI-powered content moderation</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Verified professional network</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Ad-free experience</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Priority event access</span>
                    </li>
                  </ul>
                  <Button 
                    className="w-full bg-teal-500 hover:bg-teal-600" 
                    onClick={() => handleSubscription('monthly')}
                    data-testid="monthly-subscription-button"
                  >
                    Choose Monthly
                  </Button>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-teal-500 relative bg-gradient-to-br from-teal-50 to-blue-50">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-teal-500 text-white px-4 py-1">BEST VALUE - 20% OFF</Badge>
                </div>
                <CardContent className="p-8 text-center">
                  <h3 className="font-semibold text-xl mb-2">Yearly</h3>
                  <div className="text-4xl font-bold mb-2">
                    AED 288
                    <span className="text-lg text-gray-500 font-normal">/year</span>
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    <span className="line-through">AED 360</span> - Save AED 72
                  </div>
                  <ul className="text-sm space-y-3 mb-8 text-left">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>AI-powered content moderation</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Verified professional network</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Ad-free experience</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Priority event access</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-500" />
                      <span className="font-medium text-teal-700">2 months free</span>
                    </li>
                  </ul>
                  <Button 
                    className="w-full bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700"
                    onClick={() => handleSubscription('yearly')}
                    data-testid="yearly-subscription-button"
                  >
                    Choose Yearly - Save 20%
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center text-sm text-gray-600">
              <p>14-day free trial • Cancel anytime • Secure payment with Stripe</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Settings Dialog */}
      <Dialog open={showProfileSettings} onOpenChange={setShowProfileSettings}>
        <DialogContent 
          data-testid="profile-settings-dialog" 
          aria-describedby="profile-settings-description"
          className="max-w-2xl max-h-[85vh] flex flex-col"
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Profile Settings</DialogTitle>
          </DialogHeader>
          <div id="profile-settings-description" className="flex-1 overflow-y-auto">
            <ProfileSettings 
              user={user}
              onClose={() => setShowProfileSettings(false)}
              onUpdate={handleProfileUpdate}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Connections List Dialog */}
      <Dialog open={showFollowersList} onOpenChange={setShowFollowersList}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Connections ({followersList.length})
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Search Input */}
            <div className="relative mb-4 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search connections..."
                value={followersSearch}
                onChange={(e) => {
                  setFollowersSearch(e.target.value);
                  fetchFollowersList(e.target.value);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            
            {/* Connections List */}
            <div className="flex-1 overflow-y-auto">
              {followersList.map(follower => (
                <div key={follower.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg">
                  <Avatar className="w-12 h-12">
                    {follower.profile_image ? (
                      <AvatarImage src={`${API_BASE}/users/profile-image/${follower.profile_image}`} alt={follower.full_name} />
                    ) : (
                      <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white">
                        {follower.full_name.charAt(0)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => fetchUserProfile(follower.id)}
                        className="font-semibold truncate hover:text-teal-600 cursor-pointer transition-colors"
                      >
                        {follower.full_name}
                      </button>
                      {follower.linkedin_verified && (
                        <Badge className="bg-[#0077B5] text-white text-xs">
                          <Linkedin className="w-3 h-3 mr-1" />
                          IN
                        </Badge>
                      )}
                      {follower.uaepass_verified && (
                        <Badge className="bg-green-600 text-white text-xs">
                          <Shield className="w-3 h-3 mr-1" />
                          UAE
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">@{follower.username}</p>
                    <p className="text-xs text-gray-400">{follower.followers_count} connections</p>
                    {follower.location && <p className="text-xs text-gray-400">{follower.location}</p>}
                  </div>
                </div>
              ))
              }
              
              {followersList.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  {followersSearch ? (
                    <p>No connections found for "{followersSearch}"</p>
                  ) : (
                    <p>No connections yet</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Connected List Dialog */}
      <Dialog open={showFollowingList} onOpenChange={setShowFollowingList}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5" />
              Connected ({followingList.length})
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Search Input */}
            <div className="relative mb-4 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search connected users..."
                value={followingSearch}
                onChange={(e) => {
                  setFollowingSearch(e.target.value);
                  fetchFollowingList(e.target.value);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            
            {/* Connected List */}
            <div className="flex-1 overflow-y-auto">
              {followingList.map(following => (
                <div key={following.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg">
                  <Avatar className="w-12 h-12">
                    {following.profile_image ? (
                      <AvatarImage src={`${API_BASE}/users/profile-image/${following.profile_image}`} alt={following.full_name} />
                    ) : (
                      <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white">
                        {following.full_name.charAt(0)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => fetchUserProfile(following.id)}
                        className="font-semibold truncate hover:text-teal-600 cursor-pointer transition-colors"
                      >
                        {following.full_name}
                      </button>
                      {following.linkedin_verified && (
                        <Badge className="bg-[#0077B5] text-white text-xs">
                          <Linkedin className="w-3 h-3 mr-1" />
                          IN
                        </Badge>
                      )}
                      {following.uaepass_verified && (
                        <Badge className="bg-green-600 text-white text-xs">
                          <Shield className="w-3 h-3 mr-1" />
                          UAE
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">@{following.username}</p>
                    <p className="text-xs text-gray-400">{following.followers_count} connections</p>
                    {following.location && <p className="text-xs text-gray-400">{following.location}</p>}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => unfollowUser(following.id)}
                    className="text-xs px-3 py-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Disconnect
                  </Button>
                </div>
              ))
              }
              
              {followingList.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Heart className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  {followingSearch ? (
                    <p>No users found for "{followingSearch}"</p>
                  ) : (
                    <p>Not connected to anyone yet</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* My Stories Modal with Tabs */}
      <Dialog open={showWeeklyStories} onOpenChange={setShowWeeklyStories}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              My Stories ({weeklyStories.length})
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Tab Navigation */}
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-4 flex-shrink-0">
              <button
                onClick={() => {
                  setActiveStoriesTab('verified');
                  fetchWeeklyStories('verified', storiesSearch);
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  activeStoriesTab === 'verified'
                    ? 'bg-white text-green-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                data-testid="verified-stories-tab"
              >
                <div className="flex items-center justify-center gap-2">
                  <Check className="w-4 h-4" />
                  Verified Stories
                </div>
              </button>
              <button
                onClick={() => {
                  setActiveStoriesTab('rejected');
                  fetchWeeklyStories('rejected', storiesSearch);
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  activeStoriesTab === 'rejected'
                    ? 'bg-white text-red-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                data-testid="rejected-stories-tab"
              >
                <div className="flex items-center justify-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Rejected Stories
                </div>
              </button>
            </div>

            {/* Search Input */}
            <div className="relative mb-4 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={`Search ${activeStoriesTab} stories...`}
                value={storiesSearch}
                onChange={(e) => {
                  setStoriesSearch(e.target.value);
                  fetchWeeklyStories(activeStoriesTab, e.target.value);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            
            {/* Stories List */}
            <div className="flex-1 overflow-y-auto space-y-4">
              {weeklyStories.length > 0 ? (
                weeklyStories.map((story) => (
                  <div key={story.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                    activeStoriesTab === 'rejected' ? 'border-red-200 bg-red-50' : 'border-gray-200'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <Avatar className="w-10 h-10">
                        {story.author_profile_image ? (
                          <AvatarImage src={`${API_BASE}/users/profile-image/${story.author_profile_image}`} alt={story.author_full_name} />
                        ) : (
                          <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white">
                            {story.author_full_name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-gray-900">{story.author_full_name}</span>
                            <span className="text-gray-500 text-sm">
                              {new Date(story.created_at).toLocaleDateString()} at {new Date(story.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                          {activeStoriesTab === 'verified' ? (
                            <Badge className="bg-green-100 text-green-800">
                              <Check className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">
                              <XCircle className="w-3 h-3 mr-1" />
                              Rejected
                            </Badge>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <p className="text-gray-700 whitespace-pre-wrap">{story.content}</p>
                          
                          {/* Show rejection reason for rejected stories */}
                          {activeStoriesTab === 'rejected' && story.moderation_reason && (
                            <div className="bg-red-100 border border-red-200 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <AlertCircle className="w-4 h-4 text-red-600" />
                                <span className="text-sm font-medium text-red-800">Rejection Reason:</span>
                              </div>
                              <p className="text-sm text-red-700">{story.moderation_reason}</p>
                            </div>
                          )}
                          
                          {/* Story Images */}
                          {story.images && story.images.length > 0 && (
                            <div className={`grid gap-2 ${
                              story.images.length === 1 ? 'grid-cols-1' : 
                              story.images.length === 2 ? 'grid-cols-2' : 
                              'grid-cols-2 md:grid-cols-3'
                            }`}>
                              {story.images.map((image, index) => (
                                <img
                                  key={index}
                                  src={image}
                                  alt={`Story image ${index + 1}`}
                                  className="rounded-lg object-cover w-full h-32 sm:h-40"
                                />
                              ))}
                            </div>
                          )}
                          
                          {/* Story Engagement - only for verified stories */}
                          {activeStoriesTab === 'verified' && (
                            <div className="flex items-center space-x-4 pt-2 border-t border-gray-100">
                              <div className="flex items-center space-x-1 text-gray-500">
                                <Heart className="w-4 h-4" />
                                <span className="text-sm">{story.likes?.length || 0}</span>
                              </div>
                              <div className="flex items-center space-x-1 text-gray-500">
                                <MessageCircle className="w-4 h-4" />
                                <span className="text-sm">{story.comments?.length || 0}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  {activeStoriesTab === 'verified' ? (
                    <Check className="w-16 h-16 text-green-300 mx-auto mb-4" />
                  ) : (
                    <XCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
                  )}
                  {storiesSearch ? (
                    <>
                      <p className="text-gray-500 mb-2">No {activeStoriesTab} stories found for "{storiesSearch}"</p>
                      <p className="text-sm text-gray-400">Try different keywords or check the other tab</p>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-500 mb-2">
                        {activeStoriesTab === 'verified' ? 'No verified stories yet' : 'No rejected stories'}
                      </p>
                      <p className="text-sm text-gray-400">
                        {activeStoriesTab === 'verified' 
                          ? 'Create your first story to get started!' 
                          : 'Great! All your stories have been approved.'}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Community Dialog */}
      <Dialog open={showCreateCommunity} onOpenChange={setShowCreateCommunity}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-600" />
              Create New Community
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="community-name">Community Name *</Label>
              <Input
                id="community-name"
                value={newCommunity.name}
                onChange={(e) => setNewCommunity({ ...newCommunity, name: e.target.value })}
                placeholder="e.g., Dubai Tech Entrepreneurs"
                data-testid="community-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="community-description">Description *</Label>
              <Textarea
                id="community-description"
                value={newCommunity.description}
                onChange={(e) => setNewCommunity({ ...newCommunity, description: e.target.value })}
                placeholder="Describe what your community is about..."
                rows={3}
                data-testid="community-description-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreateCommunity(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={createCommunity}
                disabled={!newCommunity.name.trim() || !newCommunity.description.trim()}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
                data-testid="create-community-submit"
              >
                Create Community
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={showCreateEvent} onOpenChange={setShowCreateEvent}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Create New Event
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="event-title">Event Title *</Label>
              <Input
                id="event-title"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                placeholder="e.g., Dubai Startup Networking"
                data-testid="event-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-description">Description *</Label>
              <Textarea
                id="event-description"
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Describe your event..."
                rows={3}
                data-testid="event-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-date">Date & Time *</Label>
              <Input
                id="event-date"
                type="datetime-local"
                value={newEvent.event_date}
                onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                data-testid="event-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-location">Location (Optional)</Label>
              <Input
                id="event-location"
                value={newEvent.location}
                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                placeholder="e.g., DIFC, Dubai"
                data-testid="event-location-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreateEvent(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={createEvent}
                disabled={!newEvent.title.trim() || !newEvent.description.trim() || !newEvent.event_date}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="create-event-submit"
              >
                Create Event
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Profile Modal */}
      <Dialog open={showUserProfile} onOpenChange={setShowUserProfile}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              User Profile
            </DialogTitle>
          </DialogHeader>
          
          {selectedUserProfile && (
            <div className="flex-1 overflow-y-auto space-y-6">
              {/* Profile Header */}
              <div className="text-center space-y-4">
                <Avatar className="w-24 h-24 mx-auto border-4 border-white shadow-lg">
                  {selectedUserProfile.profile_image ? (
                    <AvatarImage 
                      src={`${API_BASE}/users/profile-image/${selectedUserProfile.profile_image}`} 
                      alt={selectedUserProfile.full_name} 
                    />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-r from-teal-500 to-blue-600 text-white text-3xl">
                      {selectedUserProfile.full_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  )}
                </Avatar>

                {/* User Info */}
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2">
                    <h2 className="text-2xl font-bold text-gray-800">{selectedUserProfile.full_name}</h2>
                    {selectedUserProfile.linkedin_verified && (
                      <Badge className="bg-[#0077B5] text-white text-sm px-3 py-1">
                        <Linkedin className="w-4 h-4 mr-1" />
                        LinkedIn
                      </Badge>
                    )}
                    {selectedUserProfile.uaepass_verified && (
                      <Badge className="bg-green-600 text-white text-sm px-3 py-1">
                        <Shield className="w-4 h-4 mr-1" />
                        UAE Pass
                      </Badge>
                    )}
                  </div>
                  <p className="text-gray-600">@{selectedUserProfile.username}</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 py-4 border-y border-gray-200">
                  <div className="text-center">
                    <div className="font-bold text-teal-600 text-xl">{selectedUserProfile.followers?.length || 0}</div>
                    <div className="text-sm text-gray-500">Connections</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-teal-600 text-xl">{selectedUserProfile.following?.length || 0}</div>
                    <div className="text-sm text-gray-500">Connected</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-teal-600 text-xl">-</div>
                    <div className="text-sm text-gray-500">Stories</div>
                  </div>
                </div>
              </div>

              {/* Profile Details */}
              <div className="space-y-4">
                {/* About */}
                {selectedUserProfile.about_me && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">About</h3>
                    <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{selectedUserProfile.about_me}</p>
                  </div>
                )}

                {/* Location */}
                {selectedUserProfile.location && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>{selectedUserProfile.location}</span>
                  </div>
                )}

                {/* Interests */}
                {selectedUserProfile.interests && selectedUserProfile.interests.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Interests</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedUserProfile.interests.map((interest, index) => (
                        <Badge key={index} className="bg-teal-100 text-teal-800 px-3 py-1">
                          {interest}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Connection Status & Actions */}
                <div className="flex flex-col items-center gap-3 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    {selectedUserProfile.open_to_connecting !== false ? '🟢 Open to connecting' : '🔴 Taking a break'}
                  </p>
                  
                  {selectedUserProfile.id !== user?.id && (
                    <div className="flex gap-3">
                      <Button
                        onClick={() => followUser(selectedUserProfile.id)}
                        className="bg-teal-500 hover:bg-teal-600 text-white px-6"
                      >
                        Connect
                      </Button>
                      <Button
                        onClick={() => sendFollowRequest(selectedUserProfile.id)}
                        variant="outline"
                        className="text-teal-600 border-teal-200 hover:bg-teal-50 px-6"
                      >
                        Request
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg border-l-4 ${
          toast.type === 'success' 
            ? 'bg-green-50 border-green-500 text-green-800' 
            : 'bg-red-50 border-red-500 text-red-800'
        } max-w-sm`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {toast.type === 'success' ? (
                <Check className="w-5 h-5 mr-3" />
              ) : (
                <XCircle className="w-5 h-5 mr-3" />
              )}
              <p className="text-sm font-medium">{toast.message}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="ml-2 p-1 h-6 w-6"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Subscription Success Page
const SubscriptionSuccess = () => {
  const { user, token } = useAuth();
  const [status, setStatus] = useState('checking');
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      
      if (sessionId) {
        try {
          const response = await axios.get(`${API_BASE}/payments/status/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.payment_status === 'paid') {
            setStatus('success');
          } else {
            setStatus('pending');
            // Poll for status
            setTimeout(checkPaymentStatus, 3000);
          }
        } catch (error) {
          setStatus('error');
        }
      }
    };
    
    checkPaymentStatus();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center border-0 shadow-2xl" data-testid="subscription-success-page">
        <CardContent className="p-8">
          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-green-700 mb-4">Welcome to Premium!</h2>
              <p className="text-gray-600 mb-6">
                You're now part of Dubai's premier professional network
              </p>
              <div className="space-y-2 text-sm text-gray-600 mb-6">
                <p>✓ Verified social connections</p>
                <p>✓ Ad-free experience</p>
                <p>✓ Priority event access</p>
                <p>✓ AI-powered content curation</p>
              </div>
            </>
          )}
          
          {status === 'pending' && (
            <>
              <div className="animate-spin w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold mb-4">Processing Payment...</h2>
              <p className="text-gray-600">Please wait while we confirm your subscription</p>
            </>
          )}
          
          <Button 
            onClick={() => window.location.href = '/'}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="return-home-button"
          >
            Continue to SureSpace
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <div className="App dashboard-background">
      <AuthProvider>
        <Router>
          <Routes>
            <Route
              path="/login"
              element={
                <ProtectedRoute requireAuth={false}>
                  <LoginPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/auth/callback"
              element={
                <ProtectedRoute requireAuth={false}>
                  <LinkedInCallback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/success"
              element={
                <ProtectedRoute>
                  <SubscriptionSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/cancel"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = true }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_safesocial-network/artifacts/s3xb4euj_ss2.png"
              alt="SureSpace Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-teal-600 font-medium">Loading SureSpace...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" replace />;
  }

  if (!requireAuth && user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default App;